import AffiliateCodeDetail from "issara-sdk/apis/AffiliateCodeDetail_core";
import BeforePaymentCheck from "issara-sdk/apis/BeforePaymentCheck_apps_BIL";
import ConstanceView from "issara-sdk/apis/ConstanceView_core";
import CouponCodeView from "issara-sdk/apis/CouponCodeView_apps_BIL";
import InvoiceItemDetail from "issara-sdk/apis/InvoiceItemDetail_apps_BIL";
import InvoiceItemPackageApplyView from "issara-sdk/apis/InvoiceItemPackageApplyView_apps_BIL";
import InvoiceItemWithInvoiceList from "issara-sdk/apis/InvoiceItemWithInvoiceList_apps_BIL";
import MasterAccountList from "issara-sdk/apis/MasterAccountList_apps_BILM";
import MasterEDCList from "issara-sdk/apis/MasterEDCList_apps_BILM";
import PaymentCalculateView from "issara-sdk/apis/PaymentCalculateView_apps_BIL";
import ReceiptByItemList from "issara-sdk/apis/ReceiptByItemList_apps_BIL";
import ReceiptByItemWithInvoiceList from "issara-sdk/apis/ReceiptByItemWithInvoiceList_apps_BIL";
import VoucherAvailableListView from "issara-sdk/apis/VoucherAvailableListView_apps_BILM";
import VoucherCheckCodeView from "issara-sdk/apis/VoucherCheckCodeView_apps_BIL";
import WalletList from "issara-sdk/apis/WalletList_apps_BIL";
import WalletTypeList from "issara-sdk/apis/WalletTypeList_apps_BIL";

// serializer
import ReceiptByItemSerializer from "issara-sdk/types/ReceiptByItemSerializer_apps_BIL";
import VoucherAvailableSerializer from "issara-sdk/types/VoucherAvailableSerializer_apps_BIL";
import WalletTypeSerializer from "issara-sdk/types/WalletTypeSerializer_apps_BIL";

import {
  ActionHandler,
  BaseSeqState,
  BLC_STATUS,
  GenerateActionTypes,
  generatePrefixedActions,
  PickMainState,
  SetErrorMessage,
} from "react-lib/apps/HISV3/common/CommonInterface";

import type { InvoiceItem, PackageApplyRow } from "../../patient/billingModelV4";
import type { PayCalc, PayMaster, Wallet } from "../../patient/billingPayModelV4";

/** ============== Type-Defining Constants =============  */

export const ACTIONS = {
  LOAD: "LOAD",
  DELETE: "DELETE",
  PKG_LOAD: "PKG_LOAD",
  PKG_APPLY: "PKG_APPLY",
  PRINT_PENDING: "PRINT_PENDING",
  PAY: "PAY",
  PAY_CALC: "PAY_CALC",
  PAY_VOUCHER: "PAY_VOUCHER",
  PAY_AFFILIATE: "PAY_AFFILIATE",
  PAY_COUPON: "PAY_COUPON",
  PAY_SUBMIT: "PAY_SUBMIT",
  PAY_CLOSE: "PAY_CLOSE",
} as const;

export const SEQUENCE_NAME = "BillingV4" as const;

export const CARD_NAME = "CardBillingV4" as const;

/** =============== Types and Interfaces ===============  */
export type CancelData = {
  username?: string;
  password?: string;
  remark?: string;
  division?: number;
};

/** ข้อมูลของ pop-up รับชำระ */
export type PayState = {
  items: InvoiceItem[];
  calc: PayCalc | null;
  wallets: Wallet[];
  walletTypes: Record<number, string>;
  vouchers: VoucherAvailableSerializer[];
  accounts: PayMaster[];
  edcs: PayMaster[];
  doctorId: number | null;
};

export type InvoiceDiscount = { discount_policy: null; name: string; value: string };

/** body ของ receipt-by-item ที่มาจาก pop-up (ที่เหลือ sequence เติมเอง) */
export type ReceiptData = Pick<
  ReceiptByItemSerializer,
  "payments" | "price" | "round_off" | "receipt_doctor" | "user_remark" | "vouchers"
> & {
  invoice_discounts: InvoiceDiscount[];
  patient_name_lang: string;
  affiliate: number | null;
};

export type State = Partial<{
  BillingV4Sequence: Partial<{
    sequenceIndex: "Action" | "START" | null;
    patientId: number;
    items: InvoiceItem[];
    // ชื่อ Package ที่ตัดใช้ ของแถว APPLIED
    packageNames: Record<number, string>;
    // mode_code ที่ยกเลิกได้ (BIL_CANCELABLE_BILL_MODES)
    cancelableModes: string[];
    packageItemId: number | null;
    packageRows: PackageApplyRow[];
    pay: PayState | null;
  }> | null;
}>;

export type PickedState = PickMainState<
  "buttonLoadCheck" | "errorMessage" | "bilReceiptCodeDetail"
>;

export type Event =
  | { message: "RunSequence"; params: Record<string, unknown> }
  | { message: "GetMasterData"; params: Record<string, unknown> }
  | { message: "HandleGetReceiptCode"; params: Record<string, unknown> };

export type Data = { division?: number; device?: number };

export type SeqState = BaseSeqState<SequenceName>;

export type Actions = typeof ACTIONS;

type SequenceName = typeof SEQUENCE_NAME;

type ActionPayloadMap = {
  [ACTIONS.LOAD]: { patientId: number };
  [ACTIONS.DELETE]: { item: InvoiceItem; data: CancelData; onDone?: () => void };
  [ACTIONS.PKG_LOAD]: { item: InvoiceItem };
  [ACTIONS.PKG_APPLY]: {
    item: InvoiceItem;
    data: { items: { package_order_item_id: number; quantity_self_applied: number }[] };
    onDone?: () => void;
  };
  [ACTIONS.PRINT_PENDING]: { items: InvoiceItem[] };
  [ACTIONS.PAY]: { items: InvoiceItem[] };
  [ACTIONS.PAY_CALC]: { invoiceDiscounts: InvoiceDiscount[]; vouchers: number[] };
  [ACTIONS.PAY_VOUCHER]: {
    code: string;
    onDone: (voucher: VoucherAvailableSerializer | null) => void;
  };
  [ACTIONS.PAY_AFFILIATE]: {
    code: string;
    onDone: (affiliate: { id: number; name: string } | null) => void;
  };
  [ACTIONS.PAY_COUPON]: {
    code: string;
    onDone: (value: string | null) => void;
  };
  [ACTIONS.PAY_SUBMIT]: {
    data: ReceiptData;
    receiptNo?: string;
    onDone?: (receiptCode: string) => void;
  };
  [ACTIONS.PAY_CLOSE]: {};
};

type ActionTypes = GenerateActionTypes<
  Actions,
  ActionPayloadMap,
  SequenceName,
  PickedState & State,
  SeqState
>;

type ActionType = ActionTypes["ActionType"];

export type RunSequence = ActionTypes["RunSequence"];

export type SetProp = ActionTypes["SetProp"];

export type OnEvent = ActionTypes["OnEvent"];

type Params<A extends ActionType["action"]> = ActionTypes["Params"][A];

export type Handler<P = unknown, R = void> = ActionHandler<PickedState & State, Event, Data, P, R>;

type ApiResult = [any, any, any?];

/** ===================== Constants ====================  */
export const StateInitial: State = {
  BillingV4Sequence: null,
};

export const DataInitial: Data = {};

export const BTN_ACTS = generatePrefixedActions(ACTIONS, SEQUENCE_NAME);

/** ===================== Functions ====================  */
export const Start: Handler<SeqState> = async (controller, params) => {
  controller.handleEvent({
    message: "GetMasterData",
    params: {
      masters: [
        ["invoiceCancelNote", {}],
        ["division", {}],
      ],
    },
  });

  controller.setState(
    {
      BillingV4Sequence: {
        sequenceIndex: "Action",
        items: [],
        packageNames: {},
        cancelableModes: [],
        packageItemId: null,
        packageRows: [],
        pay: null,
      },
    },
    () => {
      if ("action" in params) {
        Action(controller, params as unknown as ActionType);
      }
    },
  );

  // mode_code ที่ลบรายการได้
  const [res]: ApiResult = await ConstanceView.get({
    apiToken: controller.apiToken,
    params: { list: "BIL_CANCELABLE_BILL_MODES" },
    extra: { division: controller.data.division },
  });

  let modes: string[] = [];

  try {
    modes = JSON.parse(res?.result?.BIL_CANCELABLE_BILL_MODES || "[]");
  } catch {
    modes = [];
  }

  const state = controller.getState();

  controller.setState({
    BillingV4Sequence: { ...state.BillingV4Sequence, cancelableModes: modes },
  });
};

/** ====================================================  */
/**                        Action                         */
/** ====================================================  */
export const Action: Handler<ActionType> = async (controller, params) => {
  const actionHandlers: Partial<{ [K in ActionType["action"]]: Handler<Params<K>> }> = {
    [ACTIONS.LOAD]: HandleLoad,
    [ACTIONS.DELETE]: HandleDelete,
    [ACTIONS.PKG_LOAD]: HandlePackageLoad,
    [ACTIONS.PKG_APPLY]: HandlePackageApply,
    [ACTIONS.PRINT_PENDING]: HandlePrintPending,
    [ACTIONS.PAY]: HandlePay,
    [ACTIONS.PAY_CALC]: HandlePayCalc,
    [ACTIONS.PAY_VOUCHER]: HandlePayVoucher,
    [ACTIONS.PAY_AFFILIATE]: HandlePayAffiliate,
    [ACTIONS.PAY_COUPON]: HandlePayCoupon,
    [ACTIONS.PAY_SUBMIT]: HandlePaySubmit,
    [ACTIONS.PAY_CLOSE]: HandlePayClose,
  };

  const { action } = params;

  const handler = actionHandlers[action] as Handler<Params<typeof action>> | undefined;

  if (handler) {
    return handler(controller, params);
  }

  console.error(`No handler found for action: ${action}`);
};

/** ====================================================  */
/**                        Handler                        */
/** ====================================================  */
const HandleLoad: Handler<Params<Actions["LOAD"]>> = async (controller, params) => {
  let state = controller.getState();
  const samePatient = state.BillingV4Sequence?.patientId === params.patientId;

  controller.setState({
    BillingV4Sequence: {
      ...state.BillingV4Sequence,
      patientId: params.patientId,
      items: samePatient ? (state.BillingV4Sequence?.items ?? []) : [],
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.LOAD]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.LOAD]: null },
  });

  const [res, error]: ApiResult = await InvoiceItemWithInvoiceList.get({
    apiToken: controller.apiToken,
    params: { patient: params.patientId, strict_encounter_type: false },
    extra: { device: controller.data.device },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.LOAD, error });

    return;
  }

  const items: InvoiceItem[] = res?.items ?? [];

  // ชื่อ Package ที่ตัดใช้ของแถว APPLIED
  const applied = items.filter(
    (item) => item.package_status === "APPLIED" && item.status_name !== "CANCELED",
  );

  const applyResults: ApiResult[] = await Promise.all(
    applied.map((item) =>
      InvoiceItemPackageApplyView.get({ apiToken: controller.apiToken, pk: item.id }),
    ),
  );

  const packageNames: Record<number, string> = {};

  for (const [index, item] of applied.entries()) {
    const rows: PackageApplyRow[] = applyResults[index]?.[0]?.items ?? [];

    packageNames[item.id ?? 0] = rows
      .filter((row) => Number(row.quantity_self_applied) > 0)
      .map((row) => row.package_name)
      .join(", ");
  }

  state = controller.getState();

  if (state.BillingV4Sequence?.patientId !== params.patientId) {
    return;
  }

  controller.setState({
    BillingV4Sequence: { ...state.BillingV4Sequence, items, packageNames },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.LOAD]: BLC_STATUS.SUCCESS },
  });
};

const HandleDelete: Handler<Params<Actions["DELETE"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.DELETE]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.DELETE]: null },
  });

  const [, error]: ApiResult = await InvoiceItemDetail.delete({
    apiToken: controller.apiToken,
    pk: params.item.id,
    extra: { data: params.data, division: controller.data.division },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.DELETE, error });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...controller.getState().buttonLoadCheck,
      [BTN_ACTS.DELETE]: BLC_STATUS.SUCCESS,
    },
  });

  params.onDone?.();

  await reloadItems(controller);
};

const HandlePackageLoad: Handler<Params<Actions["PKG_LOAD"]>> = async (controller, params) => {
  let state = controller.getState();

  controller.setState({
    BillingV4Sequence: {
      ...state.BillingV4Sequence,
      packageItemId: params.item.id,
      packageRows: [],
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PKG_LOAD]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.PKG_LOAD]: null },
  });

  const [res, error]: ApiResult = await InvoiceItemPackageApplyView.get({
    apiToken: controller.apiToken,
    pk: params.item.id,
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.PKG_LOAD, error });

    return;
  }

  state = controller.getState();

  if (state.BillingV4Sequence?.packageItemId !== params.item.id) {
    return;
  }

  controller.setState({
    BillingV4Sequence: { ...state.BillingV4Sequence, packageRows: res?.items ?? [] },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PKG_LOAD]: BLC_STATUS.SUCCESS },
  });
};

const HandlePackageApply: Handler<Params<Actions["PKG_APPLY"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PKG_APPLY]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.PKG_APPLY]: null },
  });

  const [, error]: ApiResult = await InvoiceItemPackageApplyView.post({
    apiToken: controller.apiToken,
    pk: params.item.id,
    data: params.data,
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.PKG_APPLY, error });

    return;
  }

  controller.setState({
    buttonLoadCheck: {
      ...controller.getState().buttonLoadCheck,
      [BTN_ACTS.PKG_APPLY]: BLC_STATUS.SUCCESS,
    },
  });

  params.onDone?.();

  await reloadItems(controller);
};

/** เปิด pop-up รับชำระ: ตรวจก่อนชำระ แล้วโหลดยอด, Wallet, คูปอง, บัญชีรับ, เครื่องรูด */
const HandlePay: Handler<Params<Actions["PAY"]>> = async (controller, params) => {
  let state = controller.getState();
  const patientId = state.BillingV4Sequence?.patientId;
  const extra = { device: controller.data.device, division: controller.data.division };

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PAY]: BLC_STATUS.LOADING },
    errorMessage: {
      ...state.errorMessage,
      [BTN_ACTS.PAY]: null,
      [BTN_ACTS.PAY_CALC]: null,
      [BTN_ACTS.PAY_SUBMIT]: null,
    },
  });

  controller.handleEvent({ message: "GetMasterData", params: { masters: [["doctor", {}]] } });
  controller.handleEvent({ message: "HandleGetReceiptCode", params: {} });

  const [check, checkError]: ApiResult = await BeforePaymentCheck.post({
    apiToken: controller.apiToken,
    data: { items: params.items.map((item) => item.id) },
    extra,
  });

  const checkMessage = checkError || (check?.status === "error" ? check?.message : null);

  if (checkMessage) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.PAY, error: checkMessage });

    return;
  }

  const results: ApiResult[] = await Promise.all([
    PaymentCalculateView.post({
      apiToken: controller.apiToken,
      data: {
        patient: patientId,
        items: params.items.map((item) => ({ ...item, price: item.pay, payable: item.pay })),
      },
      extra,
    }),
    WalletList.list({ apiToken: controller.apiToken, params: { patient: patientId }, extra }),
    WalletTypeList.list({ apiToken: controller.apiToken, extra }),
    VoucherAvailableListView.post({
      apiToken: controller.apiToken,
      data: { patient: patientId, billables: [] },
      extra,
    }),
    MasterAccountList.list({ apiToken: controller.apiToken, extra }),
    MasterEDCList.list({ apiToken: controller.apiToken, extra }),
  ]);

  const [[calc, calcError], [wallets], [types], [vouchers], [accounts], [edcs]] = results;

  // ยอดชำระต้องมี ส่วนรายการเสริม (Wallet, voucher, บัญชีรับ, เครื่องรูด) ไม่ได้ก็เปิดต่อแบบว่าง
  if (calcError) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.PAY, error: calcError });

    return;
  }

  const walletTypes: Record<number, string> = {};
  for (const type of (types?.items ?? []) as WalletTypeSerializer[]) {
    walletTypes[type.id ?? 0] = type.name;
  }

  const doctorItem = params.items.find((item) => item.episode_doctor) ?? params.items[0];

  state = controller.getState();

  controller.setState({
    BillingV4Sequence: {
      ...state.BillingV4Sequence,
      pay: {
        items: params.items,
        calc: calc ?? null,
        wallets: ((wallets?.items ?? []) as Wallet[]).filter(
          (wallet) => wallet.active && (Number(wallet.balance) || 0) > 0,
        ),
        walletTypes,
        vouchers: vouchers?.items ?? [],
        accounts: (accounts?.items ?? []).filter(
          (item: PayMaster & { active?: boolean }) => item.active,
        ),
        edcs: (edcs?.items ?? []).filter((item: PayMaster & { active?: boolean }) => item.active),
        doctorId: doctorItem?.episode_doctor || doctorItem?.encounter_doctor || null,
      },
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PAY]: BLC_STATUS.SUCCESS },
  });
};

/** คำนวณยอดใหม่เมื่อเปลี่ยนส่วนลดท้ายบิล / คูปอง */
const HandlePayCalc: Handler<Params<Actions["PAY_CALC"]>> = async (controller, params) => {
  let state = controller.getState();
  const pay = state.BillingV4Sequence?.pay;

  if (!pay) {
    return;
  }

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PAY_CALC]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.PAY_CALC]: null },
  });

  const [res, error]: ApiResult = await PaymentCalculateView.post({
    apiToken: controller.apiToken,
    data: {
      patient: state.BillingV4Sequence?.patientId,
      items: pay.items.map((item) => ({ ...item, price: item.pay, payable: item.pay })),
      invoice_discounts: params.invoiceDiscounts,
      vouchers: params.vouchers.map(String),
    },
    extra: { division: controller.data.division },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.PAY_CALC, error });

    return;
  }

  state = controller.getState();

  if (!state.BillingV4Sequence?.pay) {
    return;
  }

  controller.setState({
    BillingV4Sequence: {
      ...state.BillingV4Sequence,
      pay: { ...state.BillingV4Sequence.pay, calc: res },
    },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PAY_CALC]: BLC_STATUS.SUCCESS },
  });
};

/** พิมพ์รายการค้างชำระ (ใบแจกแจงค่าบริการของ MeHealth) */
const HandlePrintPending: Handler<Params<Actions["PRINT_PENDING"]>> = async (
  controller,
  params,
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PRINT_PENDING]: BLC_STATUS.LOADING },
  });

  const { HandlePrintMedicalFeeDetail } = await import("react-lib/apps/HISV3/BIL/BILInterface");

  await HandlePrintMedicalFeeDetail(
    controller as unknown as Parameters<typeof HandlePrintMedicalFeeDetail>[0],
    { isMeBilling: true, data: params.items },
  );

  controller.setState({
    buttonLoadCheck: {
      ...controller.getState().buttonLoadCheck,
      [BTN_ACTS.PRINT_PENDING]: BLC_STATUS.SUCCESS,
    },
  });
};

/** คูปองส่วนลด: ไม่พบ = onDone(null) */
const HandlePayVoucher: Handler<Params<Actions["PAY_VOUCHER"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PAY_VOUCHER]: BLC_STATUS.LOADING },
  });

  const [res, error]: ApiResult = await VoucherCheckCodeView.post({
    apiToken: controller.apiToken,
    data: { code: params.code, patient: state.BillingV4Sequence?.patientId },
    extra: { division: controller.data.division },
  });

  setCheckDone(controller, BTN_ACTS.PAY_VOUCHER, error);

  params.onDone(error ? null : res);
};

const HandlePayAffiliate: Handler<Params<Actions["PAY_AFFILIATE"]>> = async (
  controller,
  params,
) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PAY_AFFILIATE]: BLC_STATUS.LOADING },
  });

  const [res, error]: ApiResult = await AffiliateCodeDetail.retrieve({
    apiToken: controller.apiToken,
    code: params.code,
    extra: { division: controller.data.division },
  });

  setCheckDone(controller, BTN_ACTS.PAY_AFFILIATE, error);

  params.onDone(res?.id ? { id: res.id, name: res.name || res.code || params.code } : null);
};

/** คูปองเงินสด: ยอดของคูปองจาก check-coupon-code */
const HandlePayCoupon: Handler<Params<Actions["PAY_COUPON"]>> = async (controller, params) => {
  const state = controller.getState();

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PAY_COUPON]: BLC_STATUS.LOADING },
  });

  const [res, error]: ApiResult = await CouponCodeView.get({
    apiToken: controller.apiToken,
    params: {
      code: params.code,
      items: (state.BillingV4Sequence?.pay?.items ?? []).map((item) => item.id).join(","),
      patient: state.BillingV4Sequence?.patientId,
    },
    extra: { device: controller.data.device, division: controller.data.division },
  });

  setCheckDone(controller, BTN_ACTS.PAY_COUPON, error);

  params.onDone(error ? null : String(res?.value ?? "0.00"));
};

/** ผลของการตรวจรหัส (คูปอง / Affiliate): ไม่พบแสดงในช่องของ pop-up */
const setCheckDone = (controller: Parameters<Handler>[0], btnAction: string, error: unknown) => {
  controller.setState({
    buttonLoadCheck: {
      ...controller.getState().buttonLoadCheck,
      [btnAction]: error ? BLC_STATUS.ERROR : BLC_STATUS.SUCCESS,
    },
  });
};

/** บันทึกรับชำระ (receipt-by-item) พิมพ์ใบเสร็จ แล้วโหลดรายการใหม่ */
const HandlePaySubmit: Handler<Params<Actions["PAY_SUBMIT"]>> = async (controller, params) => {
  let state = controller.getState();
  const pay = state.BillingV4Sequence?.pay;

  if (!pay) {
    return;
  }

  controller.setState({
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PAY_SUBMIT]: BLC_STATUS.LOADING },
    errorMessage: { ...state.errorMessage, [BTN_ACTS.PAY_SUBMIT]: null },
  });

  const data = {
    ...params.data,
    items: pay.items.map((item) => ({ id: item.id, pay: item.pay })),
    patient: state.BillingV4Sequence?.patientId,
    invoice: null,
    code: "",
    receipt_code: params.receiptNo || state.bilReceiptCodeDetail?.code || "",
    reference_code: "",
    approval_code: "",
    terminal_id: "",
    merchant_id: "",
    tx_date: null,
    tx_time: null,
    require_approval_code: false,
  };

  const api = params.receiptNo ? ReceiptByItemWithInvoiceList.post : ReceiptByItemList.post;

  const [res, error]: ApiResult = await api({
    apiToken: controller.apiToken,
    data,
    extra: { device: controller.data.device, division: controller.data.division },
  });

  if (error) {
    SetErrorMessage(controller, { ...params, btnAction: BTN_ACTS.PAY_SUBMIT, error });

    return;
  }

  // ตัดสิทธิ์อย่างเดียว (ยอด 0) ไม่มีใบเสร็จให้พิมพ์
  if (Number(params.data.price) > 0) {
    const { HandlePrintReceipt } = await import("react-lib/apps/HISV3/BIL/sequence/BillPayment");

    await HandlePrintReceipt(controller as unknown as Parameters<typeof HandlePrintReceipt>[0], {
      ...res,
      receiptType: "receipt",
      isPayment: true,
      isMeBilling: true,
    });
  }

  state = controller.getState();

  controller.setState({
    BillingV4Sequence: { ...state.BillingV4Sequence, pay: null },
    buttonLoadCheck: { ...state.buttonLoadCheck, [BTN_ACTS.PAY_SUBMIT]: BLC_STATUS.SUCCESS },
  });

  controller.handleEvent({ message: "HandleGetReceiptCode", params: {} });

  params.onDone?.(res?.code || data.receipt_code);

  await reloadItems(controller);
};

const HandlePayClose: Handler<Params<Actions["PAY_CLOSE"]>> = async (controller) => {
  const state = controller.getState();

  controller.setState({ BillingV4Sequence: { ...state.BillingV4Sequence, pay: null } });
};

const reloadItems = async (controller: Parameters<Handler>[0]) => {
  const patientId = controller.getState().BillingV4Sequence?.patientId;

  if (!patientId) {
    return;
  }

  await HandleLoad(controller, { sequence: SEQUENCE_NAME, action: ACTIONS.LOAD, patientId });
};
